import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { CellData } from '@ddt-web/data-service';
import { chain } from 'lodash';
import * as cloneDeep from 'lodash/cloneDeep';

import { TableDataUtil } from '../utils/table-data.util';

@Component({
	selector: 'ac-select-columns',
	templateUrl: './select-columns.component.html',
	styleUrls: ['./select-columns.component.scss']
})
export class SelectColumnsComponent implements OnInit {
	@Input() public columnDefs: { [key: string]: { headerData: CellData; selected: boolean } };
	private columnDefsState: { [key: string]: { headerData: CellData; selected: boolean } };
	private originColumnsObject: { [key: string]: { headerData: CellData; selected: boolean } };
	public showColumns: boolean = false;
	public allColumnsSelected: boolean = false;
	public someColumnsSelected: boolean = false;
	public groups: { name: string; status: string }[];

	@Output() public onColumnsChange: EventEmitter<{ [key: string]: { headerData: CellData; selected: boolean } }> = new EventEmitter<{
		[key: string]: { headerData: CellData; selected: boolean };
	}>();

	public groupStatus = TableDataUtil.groupStatus;

	constructor(private eRef: ElementRef, private contentService: ContentService) { }

	public ngOnInit(): void {
		this.columnDefsState = cloneDeep(this.columnDefs);
		this.originColumnsObject = cloneDeep(this.columnDefs);

		this.groups = this.getGroups();
		this.allColumnsSelected = this.getAllColumnSelectedStatus();
		this.someColumnsSelected = this.getSomeColumnSelectedStatus();
	}

	@HostListener('document:click', ['$event'])
	public onClick(event): void {
		if (!this.eRef.nativeElement.contains(event.target)) {
			this.showColumns = false;
		}
	}

	public toggleAll(): void {
		Object.keys(this.columnDefsState).forEach(columnName => {
			if (this.columnDefsState.hasOwnProperty(columnName)) {
				this.columnDefsState[columnName].selected = !this.allColumnsSelected;
			}
		});
		this.groups = this.getGroups();
		this.applyColumns();
	}

	public selectColumnsClicked($event): void {
		$event.stopPropagation();
		this.showColumns = !this.showColumns;
	}

	public applyColumns(): void {
		this.allColumnsSelected = this.getAllColumnSelectedStatus();
		this.someColumnsSelected = this.getSomeColumnSelectedStatus();
		this.onColumnsChange.emit(this.columnDefsState);
	}

	public resetColumns(): void {
		this.columnDefsState = cloneDeep(this.originColumnsObject);
		this.groups = this.getGroups();
		this.applyColumns();
	}

	/**
	 * function to generate dcr content
	 * @param {string} label group name to translate
	 */
	public getDcrContent(label: string): string {
		const labelKey = `${label.replace(/-|\s/g, '_').toUpperCase()}_LABEL`;
		return this.contentService.get(labelKey, label);
	}

	/**
	 * This is the method to not use sort in keyvalue pipe, the order will be iterate order
	 */
	public sortNull(): number {
		return 0;
	}

	public getGroups(): { name: string; status: string }[] {
		return chain(Object.values(this.columnDefsState))
			.filter(column => column.headerData.attribute['columnGroup'] && !column.headerData.attribute['pinned'])
			.map(column => column.headerData.attribute['columnGroup'])
			.uniq()
			.map(columnName => {
				return {
					name: columnName,
					status: this.getGroupStatus(columnName)
				};
			})
			.value();
	}

	private getGroupStatus(groupName: string): string {
		const allColumnsSelected = Object.values(this.columnDefsState)
			.filter(column => column.headerData.attribute['columnGroup'] === groupName)
			.every(column => !!column.selected);
		const noColumnsSelected = Object.values(this.columnDefsState)
			.filter(column => column.headerData.attribute['columnGroup'] === groupName)
			.every(column => !column.selected);

		if (allColumnsSelected) {
			return TableDataUtil.groupStatus.ALL;
		} else if (noColumnsSelected) {
			return TableDataUtil.groupStatus.NONE;
		}
		return TableDataUtil.groupStatus.SOME;
	}

	public toggleGroup(group: { name: string; status: string }): void {
		Object.keys(this.columnDefsState).forEach(columnName => {
			if (this.columnDefsState.hasOwnProperty(columnName)) {
				if (this.columnDefsState[columnName].headerData.attribute['columnGroup'] === group.name) {
					this.columnDefsState[columnName].selected = group.status !== TableDataUtil.groupStatus.ALL;
				}
			}
		});
		group.status = this.getGroupStatus(group.name);
		this.applyColumns();
	}

	public toggleColumn(column: string, group: { name: string; status: string }): void {
		this.columnDefsState[column].selected = !this.columnDefsState[column].selected;
		group.status = this.getGroupStatus(group.name);
		this.applyColumns();
	}

	public getAllColumnSelectedStatus(): boolean {
		return this.groups.every(group => group.status === TableDataUtil.groupStatus.ALL);
	}

	private getSomeColumnSelectedStatus(): boolean {
		return !this.allColumnsSelected && this.groups.some(group => group.status !== TableDataUtil.groupStatus.NONE);
	}
}
